import type { ApiServiceTransport } from '@/services/ApiService';
import { gql, type GraphqlRequesterType } from '@/services/GraphqlClient';
import {
  createDesktopCloudApiTransport,
  createDesktopCloudGraphqlRequester,
} from '@/desktop/desktopCloudTransport';
import { workAreaDataSourceRegistry } from './WorkAreaDataSourceRegistry';

/**
 * How one work area's requests reach the deployment that holds it (I-1120, T-0515 FR-3).
 *
 * `gql` answers GraphQL; `transport` carries REST for an `ApiService` subclass, and is undefined for
 * the main connection, where a subclass built with no transport already uses the main client. `key`
 * names the connection, so one instance of an API serves every work area on it.
 */
export interface IWorkAreaClients {
  readonly key: string;
  readonly gql: GraphqlRequesterType;
  readonly transport: ApiServiceTransport | undefined;
}

export interface IWorkAreaApiRegistryDependencies {
  /** The desktop binding a work area has, or null for one on the main connection. */
  desktopWorkAreaIdOf(workAreaId: string): string | null;
  desktopGql(desktopWorkAreaId: string): GraphqlRequesterType;
  desktopTransport(desktopWorkAreaId: string): ApiServiceTransport;
  mainGql: GraphqlRequesterType;
}

const MAIN_KEY = 'main';

/**
 * The work-area APIs, per connection (I-1120). Each work-area API module exports a factory over
 * `IWorkAreaClients` and a `…For(workAreaId)` that asks this registry; the registry builds one
 * instance per connection and API, and hands the same one back to every work area on that
 * connection. A work area bound to a custom connection on the desktop (ADR-153) is reached through
 * its desktop binding, the way `WorkItemDataSourceRegistry.coreForProject` already reaches its
 * work items; every other work area is on the main connection.
 *
 * ⛔ A work-area API is asked for by the work area it is about, never through a module singleton:
 * the singleton reaches the main deployment only, which does not hold a custom connection's work
 * area (`deploymentReach.guard.test.ts` lists what is still owed).
 */
export class WorkAreaApiRegistry {
  private readonly clients = new Map<string, IWorkAreaClients>();
  private readonly apis = new Map<string, unknown>();

  constructor(
    private readonly dependencies: IWorkAreaApiRegistryDependencies = {
      desktopWorkAreaIdOf: (workAreaId) =>
        workAreaDataSourceRegistry.descriptor(workAreaId)?.desktopBinding?.desktopWorkAreaId ??
        null,
      desktopGql: (desktopWorkAreaId) => createDesktopCloudGraphqlRequester(desktopWorkAreaId),
      desktopTransport: (desktopWorkAreaId) => createDesktopCloudApiTransport(desktopWorkAreaId),
      // Read when asked, not when this module loads: the API modules import this one, and a cycle
      // must not capture a binding before its module has run.
      mainGql: (query, variables) => gql(query, variables),
    },
  ) {}

  clientsFor(workAreaId: string | null | undefined): IWorkAreaClients {
    const desktopWorkAreaId = workAreaId ? this.dependencies.desktopWorkAreaIdOf(workAreaId) : null;
    const key = desktopWorkAreaId ? `desktop:${desktopWorkAreaId}` : MAIN_KEY;
    let clients = this.clients.get(key);
    if (!clients) {
      clients = desktopWorkAreaId
        ? {
            key,
            gql: this.dependencies.desktopGql(desktopWorkAreaId),
            transport: this.dependencies.desktopTransport(desktopWorkAreaId),
          }
        : { key, gql: this.dependencies.mainGql, transport: undefined };
      this.clients.set(key, clients);
    }
    return clients;
  }

  /**
   * `name`'s instance for the connection that holds `workAreaId`, built once per connection. On the
   * main connection it is `main`, the module's own singleton, so there is one main instance.
   */
  api<T>(
    workAreaId: string | null | undefined,
    name: string,
    create: (clients: IWorkAreaClients) => T,
    main?: T,
  ): T {
    const clients = this.clientsFor(workAreaId);
    if (main !== undefined && clients.key === MAIN_KEY) return main;
    const id = `${clients.key}\u0000${name}`;
    if (!this.apis.has(id)) this.apis.set(id, create(clients));
    return this.apis.get(id) as T;
  }
}

export const workAreaApis = new WorkAreaApiRegistry();

/**
 * The GraphQL requester for the connection that holds `workAreaId`, for an API written as plain
 * functions rather than an object: each function takes the work area and asks through this.
 */
export function workAreaGql(workAreaId: string | null | undefined): GraphqlRequesterType {
  return workAreaApis.clientsFor(workAreaId).gql;
}
